import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  IconButton,
  MenuItem,
  Pagination,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import MoveDownIcon from "@mui/icons-material/MoveDown";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import DashboardLayout from "../../../layouts/DashboardLayout";
import PageHeader from "../../../components/layout/PageHeader";
import CentroEquipoDetalleDialog from "../../requerimiento/components/CentroEquipoDetalleDialog";
import { Encabezado, Seccion, Rejilla, fieldSx, contenidoSx, pieSx } from "../../../components/DialogoUi";
import { mensajeErrorFlespi } from "../utils/errores";
import {
  cambiarEstadoDispositivosFlespi,
  editarDispositivoFlespi,
  eliminarDispositivosFlespi,
  listarCuentasFlespi,
  listarDispositivosFlespi,
  listarSubcuentasFlespi,
  listarTiposFlespi,
  moverDispositivosFlespi,
  obtenerDispositivoFlespi,
  registrarDispositivosFlespi,
  validarDispositivosMasivosFlespi,
} from "../services/flespiService";

const emptyCreate = {
  device_type_id: "",
  messages_rotate: "1024",
  ident: "",
  name: "",
  bulk: "",
};

const emptyEdit = {
  id: "",
  name: "",
  ident: "",
  device_type_id: "",
  messages_rotate: "1024",
  enabled: true,
  configuration: {},
};

const compactFieldSx = {
  "& .MuiInputBase-root": {
    minHeight: 34,
    height: 34,
    fontSize: "0.78rem",
  },
  "& .MuiInputBase-input": {
    py: 0.55,
  },
  "& .MuiInputLabel-root": {
    fontSize: "0.76rem",
  },
  "& .MuiSelect-select": {
    py: "6px !important",
  },
};

const getRows = (response) => {
  const body = response?.data;
  if (Array.isArray(body)) return body;
  if (Array.isArray(body?.data)) return body.data;
  return [];
};

const getTelemetry = (device, key) => {
  const telemetry = device?.telemetry || {};
  if (telemetry[key] !== undefined) return telemetry[key];
  const parts = key.split(".");
  return parts.reduce((value, part) => value?.[part], telemetry);
};

const formatTimestamp = (value) => {
  const timestamp = Number(value);
  if (!timestamp) return "-";
  return new Date(timestamp * 1000).toLocaleString("sv-SE", {
    timeZone: "America/La_Paz",
    hour12: false,
  });
};

// Los errores de Flespi llegan en inglés: se traducen (ver utils/errores.js)
const errorMessage = (error) => mensajeErrorFlespi(error);

const GENERAL_ACCOUNT_KEYS = new Set([
  "flespi|flespi_internacional",
  "flespi2|admin_flespi_2",
]);

const MAX_FLESPI_DEVICES = 1000;

const COLUMNAS = [
  { key: "alias", label: "Alias" },
  { key: "identificador", label: "Identificador" },
  { key: "modelo", label: "Modelo" },
  { key: "storage", label: "Storage" },
  { key: "latitud", label: "Latitud" },
  { key: "longitud", label: "Longitud" },
  { key: "vel", label: "Vel." },
  { key: "ultimoReporte", label: "Último reporte" },
  { key: "flespi", label: "Flespi" },
  { key: "estadoSort", label: "Estado SORT" },
];

export default function FlespiDevicesPage() {
  const navigate = useNavigate();
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const requestedAccountKey = useMemo(() => {
    const origen = searchParams.get("origen") || "";
    const tipo = searchParams.get("tipo") || "";
    return origen && tipo ? `${origen}|${tipo}` : "";
  }, [searchParams]);
  const [accounts, setAccounts] = useState([]);
  const [accountKey, setAccountKey] = useState("");
  const [types, setTypes] = useState([]);
  const [subaccounts, setSubaccounts] = useState([]);
  const [devices, setDevices] = useState([]);
  const [total, setTotal] = useState(0);
  const [accountDeviceTotal, setAccountDeviceTotal] = useState(null);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(25);
  const [search, setSearch] = useState("");
  const [searchApplied, setSearchApplied] = useState("");
  const [status, setStatus] = useState("Todos");
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [createOpen, setCreateOpen] = useState(false);
  const [createMode, setCreateMode] = useState("single");
  const [editOpen, setEditOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [equipoDialog, setEquipoDialog] = useState(null);
  const [createForm, setCreateForm] = useState(emptyCreate);
  const [editForm, setEditForm] = useState(emptyEdit);
  const [bulkPreview, setBulkPreview] = useState([]);
  const [validatingBulk, setValidatingBulk] = useState(false);
  const [subaccountId, setSubaccountId] = useState("");
  const [saving, setSaving] = useState(false);
  const [dialogError, setDialogError] = useState(""); // error de Flespi mostrado dentro del diálogo abierto
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const account = useMemo(
    () => accounts.find((item) => `${item.origen}|${item.nombre}` === accountKey) || null,
    [accountKey, accounts]
  );

  const isGeneralAccount = useMemo(() => {
    if (!account) return false;
    const key = `${account.origen}|${account.nombre}`;
    const detail = String(account.detalle || "").trim().toLowerCase();
    return GENERAL_ACCOUNT_KEYS.has(key) || detail === "general";
  }, [account]);

  const typeMap = useMemo(
    () => new Map(types.map((item) => [String(item.id), item.nombre])),
    [types]
  );

  const selectedDevices = useMemo(
    () => devices.filter((device) => selected.has(String(device.id))),
    [devices, selected]
  );

  // El orden se pide al backend (sortKey/sortDir) y se aplica sobre el
  // listado completo de la cuenta antes de paginar — no solo sobre la
  // pagina visible — asi que un cambio de orden implica recargar.
  const handleSort = (key) => {
    setPage(0);
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const notify = useCallback((severity, message) => {
    setToast({ open: true, severity, message });
  }, []);

  const loadAccounts = useCallback(async () => {
    try {
      const response = await listarCuentasFlespi();
      const rows = getRows(response);
      setAccounts(rows);
      setAccountKey((current) => {
        if (current) return current;
        if (slug) {
          const bySlug = rows.find((item) => item.slug === slug);
          if (bySlug) return `${bySlug.origen}|${bySlug.nombre}`;
        }
        if (requestedAccountKey && rows.some((item) => `${item.origen}|${item.nombre}` === requestedAccountKey)) {
          return requestedAccountKey;
        }
        const first = rows.find((item) => item.rol !== "admin") || rows[0];
        return first ? `${first.origen}|${first.nombre}` : "";
      });
    } catch (error) {
      notify("error", errorMessage(error));
    }
  }, [notify, requestedAccountKey, slug]);

  const loadCatalogs = useCallback(async () => {
    if (!account?.origen) return;
    try {
      const [typesResponse, subaccountsResponse] = await Promise.all([
        listarTiposFlespi(account.origen),
        listarSubcuentasFlespi(account.origen),
      ]);
      setTypes(getRows(typesResponse));
      setSubaccounts(getRows(subaccountsResponse));
    } catch (error) {
      notify("error", errorMessage(error));
    }
  }, [account?.origen, notify]);

  const loadDevices = useCallback(async () => {
    if (!account?.origen || !account?.nombre) {
      setDevices([]);
      setTotal(0);
      return;
    }
    setLoading(true);
    try {
      const response = await listarDispositivosFlespi({
        origen: account.origen,
        tipo: account.nombre,
        page: page + 1,
        limit,
        search: searchApplied,
        status,
        sortKey: sortConfig.key || "",
        sortDir: sortConfig.direction,
      });
      const body = response?.data || {};
      setDevices(Array.isArray(body.data) ? body.data : []);
      setTotal(Number(body.total || 0));
      setSelected(new Set());
    } catch (error) {
      setDevices([]);
      setTotal(0);
      notify("error", errorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [account, limit, notify, page, searchApplied, status, sortConfig]);

  const loadAccountDeviceTotal = useCallback(async () => {
    if (!account?.origen || !account?.nombre) {
      setAccountDeviceTotal(null);
      return;
    }

    try {
      const response = await listarDispositivosFlespi({
        origen: account.origen,
        tipo: account.nombre,
        page: 1,
        limit: 1,
        search: "",
        status: "Todos",
      });
      setAccountDeviceTotal(Number(response?.data?.total || 0));
    } catch (_error) {
      setAccountDeviceTotal(null);
    }
  }, [account]);

  useEffect(() => { loadAccounts(); }, [loadAccounts]);
  useEffect(() => { loadCatalogs(); }, [loadCatalogs]);
  useEffect(() => { loadDevices(); }, [loadDevices]);
  useEffect(() => { loadAccountDeviceTotal(); }, [loadAccountDeviceTotal]);

  useEffect(() => {
    if (!slug || !accounts.length) return;
    const bySlug = accounts.find((item) => item.slug === slug);
    if (!bySlug) return;
    const nextKey = `${bySlug.origen}|${bySlug.nombre}`;
    if (nextKey !== accountKey) {
      setAccountKey(nextKey);
      setPage(0);
    }
  }, [accountKey, accounts, slug]);

  const resetCreateState = () => {
    setCreateForm(emptyCreate);
    setCreateMode("single");
    setBulkPreview([]);
    setValidatingBulk(false);
  };

  const openCreateDialog = (mode = "single") => {
    if (addDisabledReason) {
      notify("warning", addDisabledReason);
      return;
    }
    setCreateMode(mode);
    setCreateForm(emptyCreate);
    setBulkPreview([]);
    setDialogError("");
    setCreateOpen(true);
  };

  const closeCreateDialog = () => {
    if (saving) return;
    setCreateOpen(false);
    resetCreateState();
  };

  const toggleSelected = (id) => {
    const value = String(id);
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  };

  const toggleAll = () => {
    const visible = devices.map((device) => String(device.id));
    const allSelected = visible.length > 0 && visible.every((id) => selected.has(id));
    setSelected(allSelected ? new Set() : new Set(visible));
  };

  const parseDevices = () => {
    const lines = createMode === "bulk"
      ? String(createForm.bulk || "").split(/\r?\n/).filter((line) => line.trim())
      : [`${createForm.ident}\t${createForm.name}`];

    return lines.map((line, index) => {
      const columns = line.split(/\t|;/).map((value) => value.trim());
      return {
        ident: columns[0] || "",
        name: columns[1] || "",
        device_type_id: createForm.device_type_id,
        messages_rotate: createForm.messages_rotate,
        lineNumber: index + 1,
      };
    });
  };

  const handlePreviewBulk = async () => {
    const rows = parseDevices();
    if (!rows.length) {
      notify("warning", "Pega al menos una fila desde Excel.");
      return;
    }
    const invalidRow = rows.find((row) => !row.ident || !row.name);
    if (invalidRow) {
      const missing = !invalidRow.ident ? "identificador" : "nombre";
      notify("warning", `Linea ${invalidRow.lineNumber}: falta el ${missing}. Usa: Identificador;Nombre`);
      return;
    }

    setValidatingBulk(true);
    setDialogError("");
    setBulkPreview([]);
    try {
      const response = await validarDispositivosMasivosFlespi({
        devices: rows.map(({ ident, name, lineNumber }) => ({
          ident,
          name,
          lineNumber,
        })),
      });
      setBulkPreview(response?.data?.data || []);
    } catch (error) {
      setDialogError(errorMessage(error));
    } finally {
      setValidatingBulk(false);
    }
  };

  const handleCreate = async () => {
    if (addDisabledReason) {
      notify("warning", addDisabledReason);
      return;
    }
    const rows = parseDevices();
    if (!account) {
      notify("warning", "Selecciona una cuenta Flespi.");
      return;
    }
    if (!createForm.device_type_id) {
      notify("warning", "Selecciona el tipo de dispositivo.");
      return;
    }
    const invalidRow = rows.find((row) => !row.ident || !row.name);
    if (invalidRow) {
      if (createMode === "bulk") {
        const missing = !invalidRow.ident ? "identificador" : "nombre";
        notify("warning", `Registro masivo linea ${invalidRow.lineNumber}: falta el ${missing}. Usa: Identificador;Nombre`);
        return;
      }
      notify(
        "warning",
        !invalidRow.ident
          ? "Ingresa el identificador del modem."
          : "Ingresa el nombre del dispositivo."
      );
      return;
    }
    if (createMode === "bulk") {
      const allValid = bulkPreview.length === rows.length && bulkPreview.every((row) => row.ok);
      if (!allValid) {
        notify("warning", "Primero previsualiza y corrige todas las filas.");
        return;
      }
    }
    if (accountDeviceTotal !== null && Number(accountDeviceTotal) + rows.length > MAX_FLESPI_DEVICES) {
      notify("warning", `No se pueden agregar ${rows.length} dispositivo(s). La cuenta tiene ${accountDeviceTotal} y el maximo es ${MAX_FLESPI_DEVICES}.`);
      return;
    }
    setSaving(true);
    setDialogError("");
    try {
      await registrarDispositivosFlespi({
        origen: account.origen,
        tipo: account.nombre,
        devices: rows,
      });
      setCreateOpen(false);
      resetCreateState();
      notify("success", `${rows.length} dispositivo(s) registrados.`);
      loadDevices();
      loadAccountDeviceTotal();
    } catch (error) {
      setDialogError(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const openEdit = async (device) => {
    setDialogError("");
    setEditOpen(true);
    setSaving(true);
    try {
      const response = await obtenerDispositivoFlespi(device.id, {
        origen: account.origen,
        tipo: account.nombre,
      });
      const row = response?.data?.result?.[0] || device;
      setEditForm({
        id: row.id,
        name: row.name || "",
        ident: row.configuration?.ident || "",
        device_type_id: String(row.device_type_id || ""),
        messages_rotate: String(row.messages_rotate || 1024),
        enabled: row.enabled !== false,
        configuration: row.configuration || {},
      });
    } catch (error) {
      notify("error", errorMessage(error));
      setEditOpen(false);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async () => {
    if (!editForm.name || !editForm.ident || !editForm.device_type_id) {
      notify("warning", "Nombre, identificador y tipo son obligatorios.");
      return;
    }
    setSaving(true);
    setDialogError("");
    try {
      await editarDispositivoFlespi(editForm.id, {
        origen: account.origen,
        tipo: account.nombre,
        name: editForm.name,
        enabled: editForm.enabled,
        device_type_id: Number(editForm.device_type_id),
        messages_rotate: Number(editForm.messages_rotate || 1024),
        configuration: {
          ...editForm.configuration,
          ident: editForm.ident,
        },
      });
      setEditOpen(false);
      notify("success", "Dispositivo actualizado.");
      loadDevices();
    } catch (error) {
      setDialogError(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const changeEnabled = async (enabled, ids = [...selected]) => {
    if (!ids.length || !account) return;
    const idSet = new Set(ids.map(String));
    const actionDevices = devices
      .filter((device) => idSet.has(String(device.id)))
      .map((device) => ({
        id: device.id,
        name: device.name,
        ident: device.configuration?.ident || "",
      }));
    setSaving(true);
    try {
      await cambiarEstadoDispositivosFlespi({
        origen: account.origen,
        tipo: account.nombre,
        ids,
        enabled,
        devices: actionDevices,
      });
      notify("success", enabled ? "Dispositivos habilitados." : "Dispositivos deshabilitados.");
      loadDevices();
    } catch (error) {
      notify("error", errorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const handleMove = async () => {
    if (!subaccountId || !selected.size) return;
    setSaving(true);
    setDialogError("");
    try {
      await moverDispositivosFlespi({
        origen: account.origen,
        tipo: account.nombre,
        ids: [...selected],
        subaccountId,
        devices: selectedDevices.map((device) => ({
          id: device.id,
          name: device.name,
          ident: device.configuration?.ident || "",
        })),
      });
      setMoveOpen(false);
      setSubaccountId("");
      notify("success", "Dispositivos movidos a la subcuenta.");
      loadDevices();
    } catch (error) {
      setDialogError(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selected.size) return;
    setSaving(true);
    setDialogError("");
    try {
      await eliminarDispositivosFlespi({
        origen: account.origen,
        tipo: account.nombre,
        ids: [...selected],
        devices: selectedDevices.map((device) => ({
          name: device.name,
          ident: device.configuration?.ident,
        })),
      });
      setConfirmDelete(false);
      notify("success", "Dispositivos eliminados.");
      loadDevices();
      loadAccountDeviceTotal();
    } catch (error) {
      setDialogError(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const allVisibleSelected =
    devices.length > 0 && devices.every((device) => selected.has(String(device.id)));
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const isAccountFull = Number(accountDeviceTotal || 0) >= MAX_FLESPI_DEVICES;
  const addDisabledReason = isGeneralAccount
    ? "No se permite agregar dispositivos en la cuenta General"
    : isAccountFull
      ? `La cuenta ya tiene ${accountDeviceTotal} dispositivos. El maximo permitido es ${MAX_FLESPI_DEVICES}.`
      : "";

  return (
    <DashboardLayout>
      <PageHeader
        title="Flespi"
        subtitle="Administración de dispositivos, telemetría y subcuentas"
        backTo="/flespi"
        actions={
          <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
            <Tooltip title={addDisabledReason}>
              <span>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => openCreateDialog("single")}
                  disabled={!account || !!addDisabledReason}
                  sx={{ textTransform: "none" }}
                >
                  Agregar dispositivo
                </Button>
              </span>
            </Tooltip>
            <Tooltip title={addDisabledReason}>
              <span>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => openCreateDialog("bulk")}
                  disabled={!account || !!addDisabledReason}
                  sx={{ textTransform: "none" }}
                >
                  Agregar masivo
                </Button>
              </span>
            </Tooltip>
          </Box>
        }
      />

      <Box
        sx={{
          p: { xs: 1, md: 1.5 },
          bgcolor: (theme) => theme.palette.background.default,
          minHeight: "calc(100vh - 110px)",
        }}
      >
        <Paper variant="outlined" sx={{ p: 0.8, mb: 1, borderRadius: 1.5 }}>
          <Box sx={{ display: "grid", alignItems: "center", gridTemplateColumns: { xs: "1fr", md: "230px minmax(220px, 460px) 135px auto auto" }, gap: 0.75 }}>
            <TextField
              select
              size="small"
              label="Cuenta"
              sx={compactFieldSx}
              value={accountKey}
              onChange={(event) => {
                const nextKey = event.target.value;
                setAccountKey(nextKey);
                setPage(0);
                const selectedAccount = accounts.find((item) => `${item.origen}|${item.nombre}` === nextKey);
                if (selectedAccount?.slug) {
                  navigate(`/flespi/dispositivos/${encodeURIComponent(selectedAccount.slug)}`, { replace: true });
                }
              }}
            >
              {accounts.map((item) => (
                <MenuItem key={`${item.origen}-${item.nombre}`} value={`${item.origen}|${item.nombre}`}>
                  {item.origen === "flespi2" ? "Flespi 2" : "Flespi 1"} · {item.detalle || item.nombre}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              label="Buscar alias o identificador"
              sx={compactFieldSx}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  setPage(0);
                  setSearchApplied(search.trim());
                }
              }}
            />
            <TextField
              select
              size="small"
              label="Estado"
              sx={compactFieldSx}
              value={status}
              onChange={(event) => {
                setStatus(event.target.value);
                setPage(0);
              }}
            >
              {["Todos", "Habilitado", "Deshabilitado"].map((item) => (
                <MenuItem key={item} value={item}>{item}</MenuItem>
              ))}
            </TextField>
            <Button
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={() => {
                setPage(0);
                setSearchApplied(search.trim());
              }}
              sx={{ textTransform: "none", minHeight: 34, height: 34, px: 1.5, fontSize: "0.78rem" }}
            >
              Buscar
            </Button>
            <Tooltip title="Actualizar listado">
              <IconButton onClick={loadDevices} disabled={loading} size="small" sx={{ width: 34, height: 34 }}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Paper>

        <Paper variant="outlined" sx={{ mb: 1, p: 0.75, borderRadius: 1.5 }}>
          <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.75 }}>
            <Typography variant="caption" sx={{ px: 0.5, fontWeight: 700 }}>
              Seleccionados: {selected.size}
            </Typography>
            <Button size="small" startIcon={<PowerSettingsNewIcon />} disabled={!selected.size || saving} onClick={() => changeEnabled(true)}>
              Habilitar
            </Button>
            <Button size="small" color="warning" startIcon={<PowerSettingsNewIcon />} disabled={!selected.size || saving} onClick={() => changeEnabled(false)}>
              Deshabilitar
            </Button>
            <Button size="small" startIcon={<MoveDownIcon />} disabled={!selected.size || saving} onClick={() => setMoveOpen(true)}>
              Mover
            </Button>
            <Button size="small" color="error" startIcon={<DeleteIcon />} disabled={!selected.size || saving} onClick={() => setConfirmDelete(true)}>
              Eliminar
            </Button>
          </Box>
        </Paper>

        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{
            borderRadius: 1.5,
            "& .MuiTableCell-root": {
              px: 0.75,
              py: 0.35,
              fontSize: "0.72rem",
              lineHeight: 1.15,
            },
            "& .MuiTableCell-head": {
              py: 0.55,
              fontSize: "0.7rem",
              bgcolor: (theme) =>
                theme.palette.mode === "dark"
                  ? theme.palette.background.paper
                  : theme.palette.grey[100],
            },
            "& .MuiTableRow-root": {
              height: 34,
            },
          }}
        >
          <Table size="small" stickyHeader sx={{ tableLayout: "auto" }}>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={allVisibleSelected}
                    indeterminate={selected.size > 0 && !allVisibleSelected}
                    onChange={toggleAll}
                  />
                </TableCell>
                {COLUMNAS.map((col) => (
                  <TableCell key={col.key} sx={{ fontWeight: 800, whiteSpace: "nowrap" }}>
                    <TableSortLabel
                      active={sortConfig.key === col.key}
                      direction={sortConfig.key === col.key ? sortConfig.direction : "asc"}
                      onClick={() => handleSort(col.key)}
                    >
                      {col.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
                <TableCell sx={{ fontWeight: 800, whiteSpace: "nowrap" }}>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={12} align="center" sx={{ py: 5 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : devices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={12} align="center" sx={{ py: 5, color: "text.secondary" }}>
                    No hay dispositivos para mostrar.
                  </TableCell>
                </TableRow>
              ) : devices.map((device) => {
                const ident = device.configuration?.ident || "";
                const latitude = getTelemetry(device, "position.latitude");
                const longitude = getTelemetry(device, "position.longitude");
                const speed = getTelemetry(device, "position.speed");
                const timestamp = getTelemetry(device, "timestamp") || getTelemetry(device, "server.timestamp");
                return (
                  <TableRow key={device.id} hover selected={selected.has(String(device.id))}>
                    <TableCell padding="checkbox">
                      <Checkbox checked={selected.has(String(device.id))} onChange={() => toggleSelected(device.id)} />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, maxWidth: 170 }}>
                      <Tooltip title={device.name || ""}>
                        <Box sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {device.name || "-"}
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        onClick={() => ident && setEquipoDialog({ tipo: "modem", identificador: ident })}
                        sx={{ p: 0, minWidth: 0, minHeight: 22, fontSize: "0.72rem", textTransform: "none", fontWeight: 800 }}
                      >
                        {ident || "-"}
                      </Button>
                    </TableCell>
                    <TableCell>{typeMap.get(String(device.device_type_id)) || device.device_type_id || "-"}</TableCell>
                    <TableCell>{device.messages_rotate ?? "-"}</TableCell>
                    <TableCell>{latitude ?? "-"}</TableCell>
                    <TableCell>{longitude ?? "-"}</TableCell>
                    <TableCell>{speed ?? "-"}</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{formatTimestamp(timestamp)}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={device.enabled ? "success" : "error"}
                        label={device.enabled ? "Habilitado" : "Deshabilitado"}
                        sx={{ height: 20, "& .MuiChip-label": { px: 0.65, fontSize: "0.65rem" } }}
                      />
                    </TableCell>
                    <TableCell>{device.modem_sort?.estado || "-"}</TableCell>
                    <TableCell>
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => openEdit(device)} sx={{ width: 26, height: 26 }}>
                          <EditIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={device.enabled ? "Deshabilitar" : "Habilitar"}>
                        <IconButton size="small" onClick={() => changeEnabled(!device.enabled, [String(device.id)])} sx={{ width: 26, height: 26 }}>
                          <PowerSettingsNewIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            rowsPerPage={limit}
            rowsPerPageOptions={[10, 25, 50, 100]}
            onPageChange={(_, value) => setPage(value)}
            onRowsPerPageChange={(event) => {
              setLimit(Number(event.target.value));
              setPage(0);
            }}
            labelRowsPerPage="Límite"
            sx={{
              minHeight: 38,
              "& .MuiTablePagination-toolbar": { minHeight: 38, px: 1 },
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                fontSize: "0.72rem",
              },
              "& .MuiTablePagination-select": { fontSize: "0.72rem" },
              "& .MuiIconButton-root": { width: 30, height: 30 },
            }}
          />
          <Box
            sx={{
              px: 1,
              py: 0.75,
              borderTop: "1px solid",
              borderColor: "divider",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Pagina {page + 1} de {totalPages}
            </Typography>
            <Pagination
              size="small"
              color="primary"
              shape="rounded"
              page={page + 1}
              count={totalPages}
              siblingCount={1}
              boundaryCount={1}
              disabled={loading || totalPages <= 1}
              onChange={(_, value) => setPage(value - 1)}
              sx={{
                "& .MuiPaginationItem-root": {
                  minWidth: 28,
                  height: 28,
                  fontSize: "0.72rem",
                },
              }}
            />
          </Box>
        </TableContainer>
      </Box>

      {/* ── Agregar dispositivo(s) ── */}
      <Dialog open={createOpen} onClose={closeCreateDialog} fullWidth maxWidth={createMode === "bulk" ? "lg" : "sm"} scroll="paper">
        <Encabezado
          titulo={createMode === "bulk" ? "Agregar dispositivos masivamente" : "Agregar dispositivo"}
          subtitulo={account ? `Cuenta: ${account.origen === "flespi2" ? "Flespi 2" : "Flespi 1"} · ${account.detalle || account.nombre}` : ""}
        />
        <DialogContent sx={contenidoSx}>
          {dialogError && <Alert severity="error" onClose={() => setDialogError("")}>{dialogError}</Alert>}

          <Seccion titulo="Tipo y almacenamiento" nota="se aplican a todos los dispositivos">
            <Rejilla cols={2}>
              <TextField select size="small" label="Tipo de dispositivo" required value={createForm.device_type_id} sx={fieldSx}
                onChange={(event) => setCreateForm((form) => ({ ...form, device_type_id: event.target.value }))}>
                {types.map((item) => <MenuItem key={item.id} value={item.id}>{item.nombre}</MenuItem>)}
              </TextField>
              <TextField size="small" label="Almacenamiento (KB)" value={createForm.messages_rotate} sx={fieldSx}
                helperText="Mensajes que Flespi guarda del dispositivo. Por defecto 1024."
                onChange={(event) => setCreateForm((form) => ({ ...form, messages_rotate: event.target.value.replace(/[^\d]/g, "") }))} />
            </Rejilla>
          </Seccion>

          {createMode === "single" ? (
            <Seccion titulo="Dispositivo" nota="el identificador es el IMEI del módem">
              <Rejilla cols={2}>
                <TextField size="small" label="Identificador" required value={createForm.ident} sx={fieldSx}
                  onChange={(event) => setCreateForm((form) => ({ ...form, ident: event.target.value }))} />
                <TextField size="small" label="Nombre" required value={createForm.name} sx={fieldSx}
                  onChange={(event) => setCreateForm((form) => ({ ...form, name: event.target.value }))} />
              </Rejilla>
            </Seccion>
          ) : (
            <>
              <Seccion titulo="Lista de dispositivos" nota="copia las columnas desde Excel">
                <TextField
                  multiline minRows={6} fullWidth size="small" required
                  label="Dispositivos"
                  placeholder={"Identificador\tNombre\n865413051850203\tYPFB-001"}
                  value={createForm.bulk}
                  sx={{ ...fieldSx, "& .MuiInputBase-input": { fontFamily: "monospace", fontSize: "0.78rem" } }}
                  onChange={(event) => {
                    setCreateForm((form) => ({ ...form, bulk: event.target.value }));
                    setBulkPreview([]);
                  }}
                  helperText={`Columnas: Identificador y Nombre (también acepta punto y coma). ${createForm.bulk.trim() ? `${createForm.bulk.split(/\r?\n/).filter((l) => l.trim()).length} fila(s) pegada(s).` : ""}`}
                />
                <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
                  <LoadingButton variant="outlined" loading={validatingBulk} onClick={handlePreviewBulk} sx={{ textTransform: "none", fontWeight: 700 }}>
                    Previsualizar y validar
                  </LoadingButton>
                </Box>
              </Seccion>

              {bulkPreview.length > 0 && (
                <Seccion
                  titulo="Validación"
                  nota={`${bulkPreview.filter((r) => r.ok).length} listo(s) · ${bulkPreview.filter((r) => !r.ok).length} con error`}
                  sinCuerpo
                >
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ "& th": { fontWeight: 700, fontSize: 11.5, color: "text.secondary" } }}>
                          <TableCell>#</TableCell>
                          <TableCell>Módem</TableCell>
                          <TableCell>Nombre</TableCell>
                          <TableCell>Validación</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {bulkPreview.map((row) => (
                          <TableRow key={`${row.lineNumber}-${row.ident}`} sx={{ bgcolor: row.ok ? undefined : "rgba(211,47,47,0.05)" }}>
                            <TableCell>{row.lineNumber}</TableCell>
                            <TableCell>
                              <Typography variant="caption" fontWeight={700}>{row.modem?.num_sim || row.ident}</Typography>
                              <Typography variant="caption" display="block" color="text.secondary">
                                {row.modem?.emai || "-"} | {row.modem?.estado || "No encontrado"}
                              </Typography>
                            </TableCell>
                            <TableCell>{row.name}</TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                color={row.ok ? "success" : "error"}
                                label={row.ok ? "Listo" : row.errors?.join(" ") || "Error"}
                                sx={{ maxWidth: 300, height: "auto", "& .MuiChip-label": { whiteSpace: "normal", py: 0.5 } }}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Seccion>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={pieSx}>
          <Button onClick={closeCreateDialog} disabled={saving} sx={{ textTransform: "none" }}>Cancelar</Button>
          <LoadingButton
            variant="contained"
            loading={saving}
            onClick={handleCreate}
            disabled={createMode === "bulk" && (!bulkPreview.length || bulkPreview.some((row) => !row.ok))}
            sx={{ textTransform: "none", fontWeight: 700, minWidth: 150 }}
          >
            {createMode === "bulk" && bulkPreview.length ? `Guardar ${bulkPreview.length} dispositivo(s)` : "Guardar"}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* ── Editar dispositivo ── */}
      <Dialog open={editOpen} onClose={() => !saving && setEditOpen(false)} fullWidth maxWidth="sm" scroll="paper">
        <Encabezado titulo="Editar dispositivo" subtitulo={editForm.name ? `${editForm.name}${editForm.ident ? ` · ${editForm.ident}` : ""}` : ""} />
        <DialogContent sx={contenidoSx}>
          {dialogError && <Alert severity="error" onClose={() => setDialogError("")}>{dialogError}</Alert>}
          {saving && !editForm.id ? <Box sx={{ py: 4, textAlign: "center" }}><CircularProgress size={24} /></Box> : (
            <>
              <Seccion titulo="Dispositivo">
                <Rejilla cols={2}>
                  <TextField size="small" label="Nombre" required value={editForm.name} sx={fieldSx}
                    onChange={(event) => setEditForm((form) => ({ ...form, name: event.target.value }))} />
                  <TextField size="small" label="Identificador" required value={editForm.ident} sx={fieldSx}
                    onChange={(event) => setEditForm((form) => ({ ...form, ident: event.target.value }))} />
                  <TextField select size="small" label="Tipo de dispositivo" required value={editForm.device_type_id} sx={fieldSx}
                    onChange={(event) => setEditForm((form) => ({ ...form, device_type_id: event.target.value }))}>
                    {types.map((item) => <MenuItem key={item.id} value={String(item.id)}>{item.nombre}</MenuItem>)}
                  </TextField>
                  <TextField size="small" label="Almacenamiento (KB)" value={editForm.messages_rotate} sx={fieldSx}
                    onChange={(event) => setEditForm((form) => ({ ...form, messages_rotate: event.target.value.replace(/[^\d]/g, "") }))} />
                </Rejilla>
              </Seccion>
              <Seccion titulo="Estado en Flespi">
                <FormControlLabel
                  control={<Checkbox size="small" checked={editForm.enabled} onChange={(event) => setEditForm((form) => ({ ...form, enabled: event.target.checked }))} />}
                  label="Dispositivo habilitado"
                  sx={{ m: 0, "& .MuiFormControlLabel-label": { fontSize: "0.85rem", fontWeight: 600 } }}
                />
                <Typography sx={{ fontSize: 12, color: "text.secondary", ml: 3.75, mt: -0.25 }}>
                  Si lo deshabilitas, Flespi deja de recibir y procesar sus mensajes.
                </Typography>
              </Seccion>
            </>
          )}
        </DialogContent>
        <DialogActions sx={pieSx}>
          <Button onClick={() => setEditOpen(false)} disabled={saving} sx={{ textTransform: "none" }}>Cancelar</Button>
          <LoadingButton variant="contained" loading={saving} onClick={handleEdit} sx={{ textTransform: "none", fontWeight: 700, minWidth: 150 }}>Guardar cambios</LoadingButton>
        </DialogActions>
      </Dialog>

      {/* ── Mover a subcuenta ── */}
      <Dialog open={moveOpen} onClose={() => !saving && setMoveOpen(false)} fullWidth maxWidth="xs" scroll="paper">
        <Encabezado titulo="Mover a subcuenta" subtitulo={`${selected.size} dispositivo${selected.size === 1 ? "" : "s"} seleccionado${selected.size === 1 ? "" : "s"}`} />
        <DialogContent sx={contenidoSx}>
          {dialogError && <Alert severity="error" onClose={() => setDialogError("")}>{dialogError}</Alert>}
          <Seccion titulo="Destino">
            <TextField select fullWidth size="small" label="Subcuenta" value={subaccountId} sx={fieldSx}
              onChange={(event) => setSubaccountId(event.target.value)}>
              {subaccounts.map((item) => (
                <MenuItem key={`${item.id_subcuenta}-${item.nombre}`} value={item.id_subcuenta}>
                  {item.detalle || item.nombre}
                </MenuItem>
              ))}
            </TextField>
          </Seccion>
          <Alert severity="info" sx={{ py: 0 }}>
            Se deshabilitarán temporalmente {selected.size} dispositivo(s), se moverán y luego se habilitarán nuevamente.
          </Alert>
        </DialogContent>
        <DialogActions sx={pieSx}>
          <Button onClick={() => setMoveOpen(false)} disabled={saving} sx={{ textTransform: "none" }}>Cancelar</Button>
          <LoadingButton variant="contained" loading={saving} disabled={!subaccountId} onClick={handleMove} sx={{ textTransform: "none", fontWeight: 700, minWidth: 130 }}>Mover</LoadingButton>
        </DialogActions>
      </Dialog>

      {/* ── Eliminar ── */}
      <Dialog open={confirmDelete} onClose={() => !saving && setConfirmDelete(false)} maxWidth="xs" fullWidth scroll="paper">
        <Encabezado titulo="Eliminar dispositivos" subtitulo="Esta acción no se puede deshacer" />
        <DialogContent sx={contenidoSx}>
          {dialogError && <Alert severity="error" onClose={() => setDialogError("")}>{dialogError}</Alert>}
          <Alert severity="warning" sx={{ py: 0 }}>
            Se eliminarán permanentemente <b>{selected.size}</b> dispositivo(s) de Flespi.
          </Alert>
          {selectedDevices.length > 0 && (
            <Seccion titulo="Se eliminarán" sinCuerpo>
              <Box sx={{ maxHeight: 180, overflow: "auto", px: 1.25, py: 0.75, display: "grid", gap: 0.25 }}>
                {selectedDevices.slice(0, 50).map((device) => (
                  <Typography key={device.id} sx={{ fontSize: 12.5 }}>
                    <b>{device.name}</b>
                    <Typography component="span" sx={{ fontSize: 11.5, color: "text.secondary", ml: 0.75, fontFamily: "monospace" }}>{device.configuration?.ident || ""}</Typography>
                  </Typography>
                ))}
                {selectedDevices.length > 50 && <Typography sx={{ fontSize: 12, color: "text.secondary" }}>y {selectedDevices.length - 50} más…</Typography>}
              </Box>
            </Seccion>
          )}
        </DialogContent>
        <DialogActions sx={pieSx}>
          <Button onClick={() => setConfirmDelete(false)} disabled={saving} sx={{ textTransform: "none" }}>Cancelar</Button>
          <LoadingButton color="error" variant="contained" loading={saving} onClick={handleDelete} sx={{ textTransform: "none", fontWeight: 700, minWidth: 130 }}>Eliminar</LoadingButton>
        </DialogActions>
      </Dialog>

      <CentroEquipoDetalleDialog
        open={!!equipoDialog}
        tipo={equipoDialog?.tipo || "modem"}
        identificador={equipoDialog?.identificador}
        title="Detalle del módem"
        onClose={() => setEquipoDialog(null)}
      />

      <Snackbar open={toast.open} autoHideDuration={4500} onClose={() => setToast((value) => ({ ...value, open: false }))}>
        <Alert severity={toast.severity} variant="filled" onClose={() => setToast((value) => ({ ...value, open: false }))}>
          {toast.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}
